import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PageShell,
  RowActions,
  SectionCard,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import { OrderFormFieldEditor } from '../components/OrderFormFieldEditor';
import {
  useOrderFormFieldMutation,
  useOrderFormFieldQuery,
  useOrderFormFieldsQuery,
} from '../hooks/useOrderFormFields';
import { ORDER_FORM_FIELD_ROLES } from '../lib/orderFormFieldRbac';
import {
  FIELD_TYPE_LABELS,
  ORDER_FORM_MALL_LABELS,
  ORDER_FORM_MALLS,
  type OrderFormFieldPayload,
  type OrderFormFieldSummary,
  type OrderFormMall,
} from '../types/orderFormField';

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 몰 탭별 목록 — 활성 몰의 항목을 조회·CRUD 한다. */
const MallFieldList = ({
  mall,
  canWrite,
  onCreate,
  onEdit,
  onDelete,
}: {
  mall: OrderFormMall;
  canWrite: boolean;
  onCreate: () => void;
  onEdit: (row: OrderFormFieldSummary) => void;
  onDelete: (row: OrderFormFieldSummary) => void;
}) => {
  const query = useOrderFormFieldsQuery(mall);
  const items = query.data ?? [];

  const columns: DataTableColumn<OrderFormFieldSummary>[] = [
    {
      key: 'sortOrder',
      header: '순서',
      width: 56,
      align: 'right',
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'label',
      header: '항목명',
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-strong)' }}>
          {row.label}
        </span>
      ),
    },
    {
      key: 'fieldType',
      header: '유형',
      width: 120,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {FIELD_TYPE_LABELS[row.fieldType]}
        </span>
      ),
    },
    {
      key: 'display',
      header: '노출',
      width: 72,
      align: 'center',
      cell: (row) =>
        row.displayEnabled ? (
          <StatusBadge label="노출" variant="info" size="sm" />
        ) : (
          <StatusBadge label="숨김" variant="neutral" size="sm" />
        ),
    },
    {
      key: 'required',
      header: '필수',
      width: 72,
      align: 'center',
      cell: (row) =>
        row.required ? (
          <StatusBadge label="필수" variant="warning" size="sm" />
        ) : (
          <StatusBadge label="선택" variant="neutral" size="sm" />
        ),
    },
    {
      key: 'maxLength',
      header: '글자수',
      width: 72,
      align: 'right',
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.maxLength}
        </span>
      ),
    },
    {
      key: 'security',
      header: '암호화/마스킹',
      width: 110,
      align: 'center',
      cell: (row) => {
        const flags = [row.encrypt ? '암호화' : null, row.mask ? '마스킹' : null].filter(
          Boolean,
        );
        return (
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {flags.length > 0 ? flags.join(' · ') : '-'}
          </span>
        );
      },
    },
    {
      key: 'updatedAt',
      header: '수정일',
      width: 96,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.updatedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      align: 'right',
      cell: (row) => {
        if (!canWrite) return null;
        const actions: RowActionItem[] = [
          { key: 'edit', label: '수정', onClick: () => onEdit(row) },
          {
            key: 'delete',
            label: '삭제',
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => onDelete(row),
          },
        ];
        return <RowActions triggerLabel="작업" actions={actions} />;
      },
    },
  ];

  return (
    <SectionCard
      title={`${ORDER_FORM_MALL_LABELS[mall]} 추가정보 항목`}
      description="해당 몰의 주문서/결제 화면에 노출되는 추가정보 항목입니다. 노출유형·필수·글자수·암호화/마스킹을 항목별로 설정합니다."
      aside={
        canWrite ? (
          <Button size="sm" onClick={onCreate} data-testid="order-form-field-add">
            <Plus size={15} aria-hidden="true" />
            항목 등록
          </Button>
        ) : undefined
      }
    >
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>추가정보 항목을 불러오지 못했습니다.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            다시 시도
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.fieldId}
          emptyState="등록된 추가정보 항목이 없습니다."
          stickyHeader
          ariaLabel={`${ORDER_FORM_MALL_LABELS[mall]} 추가정보 항목 목록`}
        />
      )}
    </SectionCard>
  );
};

/**
 * 글로벌 주문서 추가정보(통관 필드) 관리 — 다국어 몰별(영문/중문/일문) 탭.
 * 근거: §4 (04.png·05.png, 고도몰 "주문서 추가정보"). be-order admin CRUD 계약.
 * 몰 탭 → 항목 목록 → 등록/수정/삭제 다이얼로그. RBAC=SUPER_ADMIN/OPS_ADMIN.
 * 중문몰 시드 2종(居民身份证号 18자·微信号 100자)이 이 화면에서 노출/편집된다.
 */
export const OrderFormFieldsPage = () => {
  const canWrite = useHasRole(ORDER_FORM_FIELD_ROLES);

  // 중문몰 시드 2종(居民身份证号·微信号)이 즉시 노출되므로 기본 탭=중문몰.
  const [activeMall, setActiveMall] = useState<OrderFormMall>('zh-CN');
  const [editFieldId, setEditFieldId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OrderFormFieldSummary | null>(null);

  const { create, update, remove } = useOrderFormFieldMutation();
  const detailQuery = useOrderFormFieldQuery(
    editorOpen && !creating ? editFieldId : null,
  );

  const saving = create.isPending || update.isPending;

  const openCreate = () => {
    setCreating(true);
    setEditFieldId(null);
    setEditorOpen(true);
  };

  const openEdit = (row: OrderFormFieldSummary) => {
    setCreating(false);
    setEditFieldId(row.fieldId);
    setEditorOpen(true);
  };

  const handleSubmit = (payload: OrderFormFieldPayload) => {
    if (!creating && editFieldId) {
      update.mutate(
        { fieldId: editFieldId, payload },
        {
          onSuccess: () => {
            toast.success('추가정보 항목이 수정되었습니다.');
            setEditorOpen(false);
          },
          onError: (err) => toast.error(friendlyMessage(err, '수정에 실패했습니다.')),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success('추가정보 항목이 등록되었습니다.');
          setEditorOpen(false);
        },
        onError: (err) => toast.error(friendlyMessage(err, '등록에 실패했습니다.')),
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.fieldId, {
      onSuccess: () => {
        toast.success('추가정보 항목이 삭제되었습니다.');
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(friendlyMessage(err, '삭제에 실패했습니다.')),
    });
  };

  return (
    <PageShell
      title="주문서 추가정보"
      breadcrumb={[{ label: '기본 설정' }, { label: '주문서 추가정보' }]}
      description="글로벌 몰(영문/중문/일문)별 주문서 추가정보(통관 등) 항목을 등록·관리합니다. 필수 지정 시 주문서/결제 화면에 * 가 표기됩니다."
      data-testid="order-form-fields-page"
    >
      <Tabs
        value={activeMall}
        onValueChange={(v) => setActiveMall(v as OrderFormMall)}
        className="w-full"
      >
        <TabsList className="mb-4">
          {ORDER_FORM_MALLS.map((mall) => (
            <TabsTrigger
              key={mall}
              value={mall}
              data-testid={`order-form-mall-tab-${mall}`}
            >
              {ORDER_FORM_MALL_LABELS[mall]}
            </TabsTrigger>
          ))}
        </TabsList>

        {ORDER_FORM_MALLS.map((mall) => (
          <TabsContent key={mall} value={mall}>
            <MallFieldList
              mall={mall}
              canWrite={canWrite}
              onCreate={openCreate}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
            />
          </TabsContent>
        ))}
      </Tabs>

      <OrderFormFieldEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        mall={activeMall}
        // 신규=null. 편집은 상세 GET 결과를 시드(로딩 중엔 빈 폼으로 열렸다 도착 시 리셋).
        target={creating ? null : detailQuery.data ?? null}
        saving={saving}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>추가정보 항목 삭제</DialogTitle>
            <DialogDescription>
              {deleteTarget ? `'${deleteTarget.label}'` : ''} 항목을 삭제할까요? 이 작업은
              되돌릴 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                취소
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={handleDelete}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
